import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { MOCK_CAC_CERTS, MOCK_WINDOWS_CERTS } from './mockCerts';
import {
  SimulationError,
  type OpenSessionArgs,
  type SessionResult,
  type SimulatedCert,
} from './types';
import CacSimulationDialog from './CacSimulationDialog';

type PendingSession = {
  args: OpenSessionArgs;
  certs: SimulatedCert[];
  resolve: (result: SessionResult) => void;
  reject: (err: SimulationError) => void;
};

type CacSimulationContextValue = {
  openSession: (args: OpenSessionArgs) => Promise<SessionResult>;
};

const CacSimulationContext = createContext<CacSimulationContextValue | null>(
  null,
);

const NOOP_SESSION: CacSimulationContextValue = {
  openSession: async ({ certs }) => {
    const fallbackCert =
      (certs ?? MOCK_CAC_CERTS).find((c) => c.purpose === 'signing') ??
      MOCK_CAC_CERTS[1];
    return { cert: fallbackCert, pin: '000000' };
  },
};

export function useCacSimulation(): CacSimulationContextValue {
  return useContext(CacSimulationContext) ?? NOOP_SESSION;
}

type Props = { children: ReactNode };

export function CacSimulationProvider({ children }: Props) {
  const [pending, setPending] = useState<PendingSession | null>(null);
  const pendingRef = useRef<PendingSession | null>(null);
  pendingRef.current = pending;

  const openSession = useCallback(
    (args: OpenSessionArgs) =>
      new Promise<SessionResult>((resolve, reject) => {
        setPending({
          args,
          certs: args.certs ?? MOCK_CAC_CERTS,
          resolve,
          reject,
        });
      }),
    [],
  );

  const handleConfirm = useCallback((result: SessionResult) => {
    const p = pendingRef.current;
    setPending(null);
    p?.resolve(result);
  }, []);

  const handleReject = useCallback((err: SimulationError) => {
    const p = pendingRef.current;
    setPending(null);
    p?.reject(err);
  }, []);

  const value = useMemo<CacSimulationContextValue>(
    () => ({ openSession }),
    [openSession],
  );

  return (
    <CacSimulationContext.Provider value={value}>
      {children}
      <CacSimulationDialog
        open={pending !== null}
        action={pending?.args.action ?? 'approve'}
        certs={pending?.certs ?? MOCK_CAC_CERTS}
        windowsCerts={MOCK_WINDOWS_CERTS}
        onConfirm={handleConfirm}
        onReject={handleReject}
      />
    </CacSimulationContext.Provider>
  );
}
